import { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '@/lib/api';
import { toast } from 'sonner';
import { useAuth } from '@/context/AuthContext';
import {
  ChevronLeft, ChevronRight, Plus, Calendar as CalendarIcon,
  Users as UsersIcon, MapPin, Clock, Mail, Trash2, X, Check, Loader2,
  ExternalLink, Sparkles, RotateCw, Link2,
} from 'lucide-react';
import { BookingLinksDialog } from '@/components/agenda/BookingLinksDialog';
import { Button } from '@/components/ui/button';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
  addDays, addMonths, startOfMonth, endOfMonth, startOfWeek, endOfWeek,
  addWeeks, subWeeks, isSameDay, isSameMonth, format, parseISO,
  differenceInMinutes, startOfDay, endOfDay, isBefore,
} from 'date-fns';
import { fr } from 'date-fns/locale';
import './Agenda.css';

// ============================================================================
// Helpers
// ============================================================================
const HOUR_HEIGHT = 48;   // px per hour in week/day view
const DAY_START = 7;      // 7h → 21h grid
const DAY_END = 21;

const fmtHM = (iso) => {
  try { return format(parseISO(iso), 'HH:mm'); } catch { return ''; }
};
const fmtDay = (iso) => {
  try { return format(parseISO(iso), 'EEE d MMM', { locale: fr }); } catch { return ''; }
};
const fmtDayFull = (iso) => {
  try { return format(parseISO(iso), 'EEEE d MMMM yyyy', { locale: fr }); } catch { return ''; }
};

function toLocalIsoNoTZ(date) {
  // Get YYYY-MM-DDTHH:mm suitable for datetime-local input
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  const h = String(date.getHours()).padStart(2, '0');
  const mn = String(date.getMinutes()).padStart(2, '0');
  return `${y}-${m}-${d}T${h}:${mn}`;
}

function toUtcIso(localInput) {
  // datetime-local → JS Date parses in local TZ, then toISOString → UTC ISO
  if (!localInput) return null;
  return new Date(localInput).toISOString();
}

// ============================================================================
// Main page
// ============================================================================
export default function Agenda() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [view, setView] = useState('week');   // month | week | day | list
  const [anchor, setAnchor] = useState(new Date());
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [meta, setMeta] = useState({ event_types: [], statuses: [] });
  const [usersList, setUsersList] = useState([]);
  const [filterUser, setFilterUser] = useState('team');   // team | mine | userId
  const [suggestions, setSuggestions] = useState([]);
  const [suggPanelOpen, setSuggPanelOpen] = useState(false);
  const [scanRunning, setScanRunning] = useState(false);
  const [bookingOpen, setBookingOpen] = useState(false);

  const [modalOpen, setModalOpen] = useState(false);
  const [modalEvent, setModalEvent] = useState(null);   // event for details/edit
  const [modalMode, setModalMode] = useState('create'); // create | edit | view

  // Range for API
  const range = useMemo(() => {
    if (view === 'month') {
      const s = startOfWeek(startOfMonth(anchor), { locale: fr, weekStartsOn: 1 });
      const e = endOfWeek(endOfMonth(anchor), { locale: fr, weekStartsOn: 1 });
      return { start: s, end: e };
    }
    if (view === 'week') {
      const s = startOfWeek(anchor, { locale: fr, weekStartsOn: 1 });
      const e = endOfWeek(anchor, { locale: fr, weekStartsOn: 1 });
      return { start: s, end: e };
    }
    if (view === 'day') {
      return { start: startOfDay(anchor), end: endOfDay(anchor) };
    }
    // list: 60 days ahead
    return { start: startOfDay(anchor), end: addDays(anchor, 60) };
  }, [view, anchor]);

  const loadEvents = useCallback(async () => {
    setLoading(true);
    try {
      const params = {
        start: range.start.toISOString(),
        end: range.end.toISOString(),
      };
      if (filterUser === 'mine') params.mine = true;
      else if (filterUser !== 'team') params.assigned_user_id = filterUser;
      const r = await api.get('/agenda/events', { params });
      setEvents(r.data.events || []);
    } catch (e) {
      console.error(e);
      toast.error('Erreur chargement agenda');
    } finally { setLoading(false); }
  }, [range.start, range.end, filterUser]);

  useEffect(() => {
    document.title = 'Agenda — Reno Resolve';
    (async () => {
      try {
        const [m, u] = await Promise.all([
          api.get('/agenda/metadata'),
          api.get('/agenda/assignable-users'),
        ]);
        setMeta(m.data);
        setUsersList(u.data || []);
      } catch (e) { console.error(e); }
    })();
  }, []);

  useEffect(() => { loadEvents(); }, [loadEvents]);

  const loadSuggestions = useCallback(async () => {
    try {
      const r = await api.get('/agenda/suggestions', { params: { status: 'pending' } });
      setSuggestions(r.data.suggestions || []);
    } catch (e) { console.error(e); }
  }, []);

  useEffect(() => { loadSuggestions(); }, [loadSuggestions]);

  const goPrev = () => {
    if (view === 'month') setAnchor(addMonths(anchor, -1));
    else if (view === 'week') setAnchor(subWeeks(anchor, 1));
    else if (view === 'day') setAnchor(addDays(anchor, -1));
    else setAnchor(addDays(anchor, -14));
  };
  const goNext = () => {
    if (view === 'month') setAnchor(addMonths(anchor, 1));
    else if (view === 'week') setAnchor(addWeeks(anchor, 1));
    else if (view === 'day') setAnchor(addDays(anchor, 1));
    else setAnchor(addDays(anchor, 14));
  };
  const goToday = () => setAnchor(new Date());

  const openCreate = (prefillDate = null) => {
    const start = prefillDate ? new Date(prefillDate) : new Date();
    start.setMinutes(0, 0, 0);
    const end = new Date(start);
    end.setHours(end.getHours() + 1);
    setModalEvent({
      title: '', event_type: 'rdv_client', status: 'scheduled',
      start_at_local: toLocalIsoNoTZ(start),
      end_at_local: toLocalIsoNoTZ(end),
      all_day: false, location: '',
      assigned_user_ids: [], client_id: '', contact_id: '',
      project_id: '', description: '', reminder_minutes_before: '',
      reminder_email: '',
    });
    setModalMode('create');
    setModalOpen(true);
  };
  const openView = (ev) => {
    setModalEvent({
      ...ev,
      start_at_local: toLocalIsoNoTZ(new Date(ev.start_at)),
      end_at_local: toLocalIsoNoTZ(new Date(ev.end_at)),
      assigned_user_ids: ev.assigned_user_ids || [],
    });
    setModalMode('view');
    setModalOpen(true);
  };

  const rangeLabel = useMemo(() => {
    if (view === 'month') return format(anchor, 'MMMM yyyy', { locale: fr });
    if (view === 'week') {
      return `${format(range.start, 'd MMM', { locale: fr })} — ${format(range.end, 'd MMM yyyy', { locale: fr })}`;
    }
    if (view === 'day') return format(anchor, 'EEEE d MMMM yyyy', { locale: fr });
    return `${format(range.start, 'd MMM', { locale: fr })} → ${format(range.end, 'd MMM yyyy', { locale: fr })}`;
  }, [view, anchor, range.start, range.end]);

  const runScan = async () => {
    if (scanRunning) return;
    setScanRunning(true);
    try {
      const r = await api.post('/agenda/scan-mailboxes', { lookback_days: 7, max_emails: 50 });
      const total = r.data.total_suggestions || 0;
      if (r.data.no_mailbox) {
        toast.warning(r.data.error || "Aucune boîte IMAP configurée. Ajoutez-en une dans Paramètres.");
      } else if (r.data.error) {
        toast.error(r.data.error);
      } else if (total > 0) {
        toast.success(`${total} nouvelle(s) suggestion(s) détectée(s)`);
      } else {
        toast.info('Aucune nouvelle suggestion');
      }
      await loadSuggestions();
    } catch (e) {
      const detail = e?.response?.data?.detail || e?.message;
      toast.error(detail ? `Scan impossible : ${detail}` : "Erreur scan boîtes mail");
    } finally { setScanRunning(false); }
  };

  return (
    <div className="space-y-4" data-testid="agenda-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-blue-100 flex items-center justify-center">
            <CalendarIcon className="w-5 h-5 text-blue-700" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Agenda
            </h1>
            <p className="text-xs text-slate-500">Agenda société · centralise RDV, chantiers, visites, réunions</p>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Button variant="outline" size="sm" onClick={() => setSuggPanelOpen(true)}
                  data-testid="agenda-suggestions-btn">
            <Mail className="w-4 h-4 mr-1" />
            RDV détectés
            {suggestions.length > 0 && (
              <span className="ml-1.5 inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full bg-blue-600 text-white text-[10px] font-semibold">
                {suggestions.length}
              </span>
            )}
          </Button>
          <Button variant="outline" size="sm" onClick={runScan} disabled={scanRunning}
                  data-testid="agenda-scan-btn">
            {scanRunning ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <RotateCw className="w-4 h-4 mr-1" />}
            Analyser les emails
          </Button>
          <Button variant="outline" size="sm" onClick={() => setBookingOpen(true)}
                  data-testid="agenda-booking-links-btn">
            <Link2 className="w-4 h-4 mr-1" /> Liens de réservation
          </Button>
          <Button size="sm" onClick={() => openCreate()} data-testid="agenda-new-event-btn">
            <Plus className="w-4 h-4 mr-1" /> Nouvel événement
          </Button>
        </div>
      </div>

      {/* Toolbar */}
      <div className="bg-white border border-slate-200 rounded-xl p-3 flex flex-wrap items-center gap-3">
        <div className="flex items-center gap-1">
          <Button variant="outline" size="sm" onClick={goToday} data-testid="agenda-today-btn">
            Aujourd'hui
          </Button>
          <Button variant="ghost" size="sm" onClick={goPrev} data-testid="agenda-prev-btn"
                  className="px-2"><ChevronLeft className="w-4 h-4" /></Button>
          <Button variant="ghost" size="sm" onClick={goNext} data-testid="agenda-next-btn"
                  className="px-2"><ChevronRight className="w-4 h-4" /></Button>
          <div className="text-sm font-semibold text-slate-900 ml-2 capitalize">{rangeLabel}</div>
        </div>

        <div className="flex items-center gap-1 ml-auto flex-wrap">
          {['month', 'week', 'day', 'list'].map((v) => (
            <button
              key={v}
              onClick={() => setView(v)}
              data-testid={`agenda-view-${v}-btn`}
              className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
                view === v
                  ? 'bg-blue-50 text-blue-700 border border-blue-200'
                  : 'text-slate-600 hover:bg-slate-50 border border-transparent'
              }`}
            >
              {v === 'month' ? 'Mois' : v === 'week' ? 'Semaine' : v === 'day' ? 'Jour' : 'Liste'}
            </button>
          ))}
        </div>

        <div className="w-full sm:w-auto">
          <Select value={filterUser} onValueChange={setFilterUser}>
            <SelectTrigger className="w-full sm:w-56" data-testid="agenda-filter-user-select">
              <SelectValue placeholder="Toute l'équipe" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="team">Toute l'équipe</SelectItem>
              <SelectItem value="mine">Moi</SelectItem>
              {usersList.map((u) => (
                <SelectItem key={u.user_id} value={u.user_id}>{u.name || u.email}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Views */}
      {loading ? (
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-6 h-6 animate-spin text-blue-600" />
        </div>
      ) : (
        <>
          {view === 'month' && (
            <MonthView anchor={anchor} events={events} onEventClick={openView}
                       onDayClick={(d) => openCreate(d)} />
          )}
          {view === 'week' && (
            <WeekView anchor={anchor} events={events} onEventClick={openView}
                       onSlotClick={(d) => openCreate(d)} />
          )}
          {view === 'day' && (
            <DayView anchor={anchor} events={events} onEventClick={openView}
                      onSlotClick={(d) => openCreate(d)} />
          )}
          {view === 'list' && (
            <ListView events={events} onEventClick={openView} />
          )}
        </>
      )}

      <BookingLinksDialog open={bookingOpen} onOpenChange={setBookingOpen}
                          meta={meta} usersList={usersList} />

      {/* Event modal */}
      <EventModal
        open={modalOpen} onOpenChange={setModalOpen}
        mode={modalMode} setMode={setModalMode}
        eventData={modalEvent} setEventData={setModalEvent}
        meta={meta} usersList={usersList}
        onSaved={() => { setModalOpen(false); loadEvents(); }}
        navigate={navigate}
      />

      {/* Suggestions panel */}
      <SuggestionsPanel
        open={suggPanelOpen} onOpenChange={setSuggPanelOpen}
        suggestions={suggestions} usersList={usersList}
        onAccepted={() => { loadSuggestions(); loadEvents(); }}
      />
    </div>
  );
}

// ============================================================================
// Month View
// ============================================================================
function MonthView({ anchor, events, onEventClick, onDayClick }) {
  const first = startOfWeek(startOfMonth(anchor), { locale: fr, weekStartsOn: 1 });
  const days = [];
  for (let i = 0; i < 42; i++) days.push(addDays(first, i));

  const eventsByDay = useMemo(() => {
    const map = {};
    events.forEach((ev) => {
      const key = format(parseISO(ev.start_at), 'yyyy-MM-dd');
      (map[key] = map[key] || []).push(ev);
    });
    return map;
  }, [events]);

  return (
    <div className="bg-white border border-slate-200 rounded-xl overflow-hidden" data-testid="agenda-month-view">
      <div className="grid grid-cols-7 border-b border-slate-200 bg-slate-50">
        {['Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam', 'Dim'].map((d) => (
          <div key={d} className="px-3 py-2 text-xs font-semibold text-slate-600">{d}</div>
        ))}
      </div>
      <div className="grid grid-cols-7">
        {days.map((d) => {
          const key = format(d, 'yyyy-MM-dd');
          const isCurrentMonth = isSameMonth(d, anchor);
          const isToday = isSameDay(d, new Date());
          const dayEvents = (eventsByDay[key] || []).slice(0, 4);
          const extra = (eventsByDay[key] || []).length - dayEvents.length;
          return (
            <div
              key={key}
              onClick={() => onDayClick(d)}
              className={`agenda-month-cell ${!isCurrentMonth ? 'other-month' : ''} ${isToday ? 'today' : ''}`}
              data-testid={`agenda-month-cell-${key}`}
            >
              <div className="text-xs font-semibold">
                <span className="day-num inline-block">{format(d, 'd')}</span>
              </div>
              {dayEvents.map((ev) => (
                <span
                  key={ev.event_id}
                  className={`agenda-pill ${ev.status === 'cancelled' ? 'cancelled' : ''}`}
                  onClick={(e) => { e.stopPropagation(); onEventClick(ev); }}
                  style={{
                    background: ev.color?.bg, color: ev.color?.text,
                    borderLeftColor: ev.color?.border,
                  }}
                  title={ev.title}
                  data-testid={`agenda-pill-${ev.event_id}`}
                >
                  {fmtHM(ev.start_at)} · {ev.title}
                </span>
              ))}
              {extra > 0 && (
                <div className="text-[10px] text-slate-500 mt-1">+ {extra} autre{extra > 1 ? 's' : ''}</div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ============================================================================
// Week View
// ============================================================================
function WeekView({ anchor, events, onEventClick, onSlotClick }) {
  const first = startOfWeek(anchor, { locale: fr, weekStartsOn: 1 });
  const days = Array.from({ length: 7 }, (_, i) => addDays(first, i));
  const hours = Array.from({ length: DAY_END - DAY_START + 1 }, (_, i) => DAY_START + i);

  const positionEvent = (ev) => {
    const s = parseISO(ev.start_at);
    const e = parseISO(ev.end_at);
    const startMin = s.getHours() * 60 + s.getMinutes() - DAY_START * 60;
    const dur = Math.max(30, differenceInMinutes(e, s));
    return {
      top: (startMin / 60) * HOUR_HEIGHT,
      height: (dur / 60) * HOUR_HEIGHT - 2,
    };
  };

  return (
    <div data-testid="agenda-week-view">
      <div className="agenda-grid-week">
        <div className="border-b border-slate-200 bg-slate-50"></div>
        {days.map((d) => (
          <div key={d.toISOString()}
               className={`border-b border-slate-200 bg-slate-50 px-2 py-2 text-center text-xs font-semibold ${isSameDay(d, new Date()) ? 'text-blue-700' : 'text-slate-700'}`}>
            <div className="uppercase">{format(d, 'EEE', { locale: fr })}</div>
            <div className="text-base font-bold">{format(d, 'd')}</div>
          </div>
        ))}
      </div>

      <div className="agenda-grid-week border-t-0 rounded-t-none">
        <div>
          {hours.map((h) => (
            <div key={h} className="hour-col" style={{ height: HOUR_HEIGHT }}>
              {String(h).padStart(2, '0')}:00
            </div>
          ))}
        </div>
        {days.map((d) => {
          const dayKey = format(d, 'yyyy-MM-dd');
          const dayEvents = events.filter((ev) =>
            isSameDay(parseISO(ev.start_at), d)
          );
          return (
            <div key={dayKey} className="day-col"
                 style={{ height: (DAY_END - DAY_START + 1) * HOUR_HEIGHT, position: 'relative' }}
                 data-testid={`agenda-week-day-${dayKey}`}>
              {hours.map((h, i) => (
                <div
                  key={h}
                  onClick={() => { const dt = new Date(d); dt.setHours(h, 0, 0, 0); onSlotClick(dt); }}
                  style={{ position: 'absolute', top: i * HOUR_HEIGHT, left: 0, right: 0, height: HOUR_HEIGHT,
                            borderTop: '1px dashed #e2e8f0' }}
                />
              ))}
              {dayEvents.map((ev) => {
                const pos = positionEvent(ev);
                return (
                  <div key={ev.event_id}
                       className={`agenda-event ${ev.status === 'cancelled' ? 'cancelled' : ''}`}
                       style={{
                         top: pos.top, height: pos.height,
                         background: ev.color?.bg, color: ev.color?.text,
                         borderLeftColor: ev.color?.border,
                       }}
                       onClick={(e) => { e.stopPropagation(); onEventClick(ev); }}
                       data-testid={`agenda-event-${ev.event_id}`}
                       title={ev.title}>
                    <div className="event-title">{ev.title}</div>
                    <div className="event-meta">{fmtHM(ev.start_at)}–{fmtHM(ev.end_at)}</div>
                    {ev.location && <div className="event-meta">📍 {ev.location}</div>}
                  </div>
                );
              })}
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ============================================================================
// Day View
// ============================================================================
function DayView({ anchor, events, onEventClick, onSlotClick }) {
  const hours = Array.from({ length: DAY_END - DAY_START + 1 }, (_, i) => DAY_START + i);
  const dayEvents = events.filter((ev) => isSameDay(parseISO(ev.start_at), anchor));

  const positionEvent = (ev) => {
    const s = parseISO(ev.start_at);
    const e = parseISO(ev.end_at);
    const startMin = s.getHours() * 60 + s.getMinutes() - DAY_START * 60;
    const dur = Math.max(30, differenceInMinutes(e, s));
    return { top: (startMin / 60) * HOUR_HEIGHT, height: (dur / 60) * HOUR_HEIGHT - 2 };
  };

  return (
    <div className="bg-white border border-slate-200 rounded-xl p-4" data-testid="agenda-day-view">
      <div className="text-sm font-semibold text-slate-900 mb-3 capitalize">
        {fmtDayFull(anchor.toISOString())}
      </div>
      <div className="relative" style={{ height: (DAY_END - DAY_START + 1) * HOUR_HEIGHT }}>
        {hours.map((h, i) => (
          <div key={h}
               onClick={() => { const dt = new Date(anchor); dt.setHours(h, 0, 0, 0); onSlotClick(dt); }}
               style={{ position: 'absolute', top: i * HOUR_HEIGHT, left: 60, right: 0, height: HOUR_HEIGHT,
                        borderTop: '1px dashed #e2e8f0', cursor: 'pointer' }} />
        ))}
        {hours.map((h, i) => (
          <div key={`h-${h}`} className="absolute left-0 w-14 text-[11px] text-slate-400 pr-2 text-right"
               style={{ top: i * HOUR_HEIGHT - 6 }}>
            {String(h).padStart(2, '0')}:00
          </div>
        ))}
        {dayEvents.map((ev) => {
          const pos = positionEvent(ev);
          return (
            <div key={ev.event_id}
                 className={`agenda-event ${ev.status === 'cancelled' ? 'cancelled' : ''}`}
                 style={{
                   position: 'absolute', top: pos.top, height: pos.height,
                   left: 60, right: 8,
                   background: ev.color?.bg, color: ev.color?.text,
                   borderLeftColor: ev.color?.border,
                 }}
                 onClick={() => onEventClick(ev)}
                 data-testid={`agenda-event-${ev.event_id}`}>
              <div className="event-title">{ev.title}</div>
              <div className="event-meta">{fmtHM(ev.start_at)}–{fmtHM(ev.end_at)}</div>
              {ev.location && <div className="event-meta">📍 {ev.location}</div>}
              {ev.assigned_users?.length > 0 && (
                <div className="event-meta">👤 {ev.assigned_users.map((u) => u.name || u.email).join(', ')}</div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ============================================================================
// List View
// ============================================================================
function ListView({ events, onEventClick }) {
  if (events.length === 0) {
    return (
      <div className="bg-white border border-slate-200 rounded-xl p-10 text-center text-slate-500" data-testid="agenda-list-empty">
        Aucun événement dans cette période.
      </div>
    );
  }
  return (
    <div className="bg-white border border-slate-200 rounded-xl divide-y divide-slate-100" data-testid="agenda-list-view">
      {events.map((ev) => (
        <div key={ev.event_id}
             onClick={() => onEventClick(ev)}
             data-testid={`agenda-list-item-${ev.event_id}`}
             className={`flex flex-col sm:flex-row gap-2 sm:items-center px-4 py-3 hover:bg-slate-50 cursor-pointer ${ev.status === 'cancelled' ? 'opacity-50' : ''}`}>
          <div className="w-32 shrink-0">
            <div className="text-xs uppercase text-slate-500 font-semibold">{fmtDay(ev.start_at)}</div>
            <div className="text-sm font-bold text-slate-900">
              {ev.all_day ? 'Journée entière' : `${fmtHM(ev.start_at)} – ${fmtHM(ev.end_at)}`}
            </div>
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="px-2 py-0.5 rounded text-[10px] font-semibold uppercase"
                    style={{ background: ev.color?.bg, color: ev.color?.text,
                             borderLeft: `3px solid ${ev.color?.border}` }}>
                {ev.event_type_label}
              </span>
              <span className={`font-semibold ${ev.status === 'cancelled' ? 'line-through' : 'text-slate-900'}`}>
                {ev.title}
              </span>
              {ev.status === 'cancelled' && (
                <span className="text-[10px] text-red-600 font-semibold uppercase">Annulé</span>
              )}
            </div>
            <div className="mt-1 flex items-center gap-3 text-xs text-slate-500 flex-wrap">
              {ev.location && <span className="flex items-center gap-1"><MapPin className="w-3 h-3" />{ev.location}</span>}
              {ev.assigned_users?.length > 0 && (
                <span className="flex items-center gap-1">
                  <UsersIcon className="w-3 h-3" />
                  {ev.assigned_users.map((u) => u.name || u.email).join(', ')}
                </span>
              )}
              {(ev.client || ev.contact) && (
                <span className="text-slate-600">
                  {ev.client?.nom_societe || ev.client?.nom_prenom || ev.contact?.nom_prenom}
                </span>
              )}
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

// ============================================================================
// Event Modal
// ============================================================================
function EventModal({ open, onOpenChange, mode, setMode, eventData, setEventData,
                      meta, usersList, onSaved, navigate }) {
  const [saving, setSaving] = useState(false);
  const [clientsList, setClientsList] = useState([]);
  const [chantiersList, setChantiersList] = useState([]);

  useEffect(() => {
    if (!open) return;
    (async () => {
      try {
        const [c, ch] = await Promise.all([
          api.get('/contacts').catch(() => ({ data: [] })),
          api.get('/chantiers').catch(() => ({ data: [] })),
        ]);
        setClientsList(Array.isArray(c.data) ? c.data.slice(0, 200) : []);
        setChantiersList(Array.isArray(ch.data) ? ch.data.slice(0, 200) : []);
      } catch (e) { /* ignore */ }
    })();
  }, [open]);

  if (!eventData) return null;
  const isEdit = mode === 'create' || mode === 'edit';

  const toggleAssignee = (uid) => {
    setEventData((prev) => {
      const ids = prev.assigned_user_ids || [];
      return {
        ...prev,
        assigned_user_ids: ids.includes(uid)
          ? ids.filter((x) => x !== uid)
          : [...ids, uid],
      };
    });
  };

  const save = async () => {
    if (!eventData.title?.trim()) { toast.error('Titre requis'); return; }
    setSaving(true);
    try {
      const payload = {
        title: eventData.title,
        description: eventData.description || null,
        event_type: eventData.event_type,
        status: eventData.status || 'scheduled',
        start_at: toUtcIso(eventData.start_at_local),
        end_at: toUtcIso(eventData.end_at_local),
        timezone: 'Europe/Paris',
        all_day: !!eventData.all_day,
        location: eventData.location || null,
        assigned_user_ids: eventData.assigned_user_ids || [],
        client_id: eventData.client_id || null,
        contact_id: eventData.contact_id || null,
        project_id: eventData.project_id || null,
        reminder_minutes_before: eventData.reminder_minutes_before ? Number(eventData.reminder_minutes_before) : null,
        reminder_email: eventData.reminder_email || null,
      };
      if (mode === 'create') {
        await api.post('/agenda/events', payload);
        toast.success('Événement créé');
      } else {
        await api.put(`/agenda/events/${eventData.event_id}`, payload);
        toast.success('Événement mis à jour');
      }
      onSaved();
    } catch (e) {
      console.error(e);
      toast.error(e?.response?.data?.detail || 'Erreur enregistrement');
    } finally { setSaving(false); }
  };

  const cancel = async () => {
    if (!window.confirm('Annuler cet événement ?')) return;
    try {
      await api.post(`/agenda/events/${eventData.event_id}/cancel`, {});
      toast.success('Événement annulé');
      onSaved();
    } catch (e) { toast.error('Erreur annulation'); }
  };

  const remove = async () => {
    if (!window.confirm('Supprimer définitivement ?')) return;
    try {
      await api.delete(`/agenda/events/${eventData.event_id}`);
      toast.success('Supprimé');
      onSaved();
    } catch (e) { toast.error('Erreur suppression'); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="agenda-event-modal">
        <DialogHeader>
          <DialogTitle>
            {mode === 'create' ? 'Nouvel événement' : (mode === 'edit' ? 'Modifier l\'événement' : 'Détails de l\'événement')}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          <div>
            <label className="text-xs font-semibold text-slate-600 block mb-1">Titre *</label>
            <Input value={eventData.title || ''}
                   onChange={(e) => setEventData({ ...eventData, title: e.target.value })}
                   disabled={!isEdit}
                   data-testid="agenda-event-title-input" />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-semibold text-slate-600 block mb-1">Type</label>
              <Select value={eventData.event_type}
                       onValueChange={(v) => setEventData({ ...eventData, event_type: v })}
                       disabled={!isEdit}>
                <SelectTrigger data-testid="agenda-event-type-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {meta.event_types.map((t) => (
                    <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-xs font-semibold text-slate-600 block mb-1">Statut</label>
              <Select value={eventData.status || 'scheduled'}
                       onValueChange={(v) => setEventData({ ...eventData, status: v })}
                       disabled={!isEdit}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {(meta.statuses || ['scheduled', 'confirmed', 'completed', 'cancelled']).map((s) => (
                    <SelectItem key={s} value={s}>{s}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="flex items-center gap-2">
            <input type="checkbox" checked={!!eventData.all_day}
                    onChange={(e) => setEventData({ ...eventData, all_day: e.target.checked })}
                    disabled={!isEdit} id="all_day"
                    data-testid="agenda-event-all-day" />
            <label htmlFor="all_day" className="text-sm text-slate-700">Toute la journée</label>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-semibold text-slate-600 block mb-1">Début *</label>
              <Input type="datetime-local" value={eventData.start_at_local}
                      onChange={(e) => setEventData({ ...eventData, start_at_local: e.target.value })}
                      disabled={!isEdit}
                      data-testid="agenda-event-start-input" />
            </div>
            <div>
              <label className="text-xs font-semibold text-slate-600 block mb-1">Fin *</label>
              <Input type="datetime-local" value={eventData.end_at_local}
                      onChange={(e) => setEventData({ ...eventData, end_at_local: e.target.value })}
                      disabled={!isEdit}
                      data-testid="agenda-event-end-input" />
            </div>
          </div>

          <div>
            <label className="text-xs font-semibold text-slate-600 block mb-1">Lieu / adresse</label>
            <Input value={eventData.location || ''}
                   onChange={(e) => setEventData({ ...eventData, location: e.target.value })}
                   disabled={!isEdit}
                   placeholder="Adresse, mairie, chantier..."
                   data-testid="agenda-event-location-input" />
          </div>

          <div>
            <label className="text-xs font-semibold text-slate-600 block mb-1">Assigné à</label>
            <div className="flex flex-wrap gap-1.5" data-testid="agenda-event-assignees">
              {usersList.map((u) => {
                const active = (eventData.assigned_user_ids || []).includes(u.user_id);
                return (
                  <button
                    key={u.user_id}
                    disabled={!isEdit}
                    onClick={() => toggleAssignee(u.user_id)}
                    data-testid={`agenda-assignee-${u.user_id}`}
                    className={`px-2.5 py-1 rounded-full text-xs font-medium border transition-colors ${
                      active
                        ? 'bg-blue-600 text-white border-blue-600'
                        : 'bg-white text-slate-700 border-slate-300 hover:bg-slate-50'
                    } ${!isEdit ? 'opacity-70 cursor-not-allowed' : ''}`}
                  >
                    {u.name || u.email}
                  </button>
                );
              })}
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-semibold text-slate-600 block mb-1">Client / Contact</label>
              <Select value={eventData.contact_id || 'none'}
                       onValueChange={(v) => setEventData({ ...eventData, contact_id: v === 'none' ? '' : v })}
                       disabled={!isEdit}>
                <SelectTrigger data-testid="agenda-event-contact-select"><SelectValue placeholder="Aucun" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">— Aucun —</SelectItem>
                  {clientsList.map((c) => (
                    <SelectItem key={c.contact_id} value={c.contact_id}>
                      {c.entreprise || c.nom_prenom}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-xs font-semibold text-slate-600 block mb-1">Chantier</label>
              <Select value={eventData.project_id || 'none'}
                       onValueChange={(v) => setEventData({ ...eventData, project_id: v === 'none' ? '' : v })}
                       disabled={!isEdit}>
                <SelectTrigger data-testid="agenda-event-project-select"><SelectValue placeholder="Aucun" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">— Aucun —</SelectItem>
                  {chantiersList.map((ch) => (
                    <SelectItem key={ch.chantier_id} value={ch.chantier_id}>
                      {ch.nom_interne || ch.project_title || ch.chantier_id}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div>
            <label className="text-xs font-semibold text-slate-600 block mb-1">Notes / description</label>
            <textarea
              className="w-full rounded-md border border-slate-300 p-2 text-sm"
              rows={3}
              value={eventData.description || ''}
              onChange={(e) => setEventData({ ...eventData, description: e.target.value })}
              disabled={!isEdit}
              data-testid="agenda-event-description-input"
            />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-semibold text-slate-600 block mb-1">Rappel (min avant)</label>
              <Input type="number" min="0"
                      value={eventData.reminder_minutes_before || ''}
                      onChange={(e) => setEventData({ ...eventData, reminder_minutes_before: e.target.value })}
                      disabled={!isEdit}
                      placeholder="ex: 30"
                      data-testid="agenda-event-reminder-input" />
            </div>
            <div>
              <label className="text-xs font-semibold text-slate-600 block mb-1">
                Email de rappel <span className="text-slate-400 font-normal">(envoi J-1 et H-2)</span>
              </label>
              <Input type="email"
                      value={eventData.reminder_email || ''}
                      onChange={(e) => setEventData({ ...eventData, reminder_email: e.target.value })}
                      disabled={!isEdit}
                      placeholder="destinataire@exemple.fr"
                      data-testid="agenda-event-reminder-email-input" />
              <p className="text-[10px] text-slate-500 mt-1">
                Si renseigné, deux rappels partent automatiquement : 24h et 2h avant.
              </p>
            </div>
          </div>

          {mode === 'view' && (
            <div className="flex flex-wrap gap-2 pt-2 border-t border-slate-100">
              {eventData.contact_id && (
                <Button variant="outline" size="sm"
                         onClick={() => navigate(`/contacts/${eventData.contact_id}`)}
                         data-testid="agenda-open-contact-btn">
                  <ExternalLink className="w-4 h-4 mr-1" /> Voir le contact
                </Button>
              )}
              {eventData.project_id && (
                <Button variant="outline" size="sm"
                         onClick={() => navigate(`/chantiers/${eventData.project_id}`)}
                         data-testid="agenda-open-chantier-btn">
                  <ExternalLink className="w-4 h-4 mr-1" /> Voir le chantier
                </Button>
              )}
              {eventData.source && (
                <span className="ml-auto text-xs text-slate-400 self-center">
                  Source: {eventData.source}
                </span>
              )}
            </div>
          )}
        </div>

        <div className="flex justify-between gap-2 pt-4 border-t border-slate-100 mt-4">
          <div className="flex gap-2">
            {mode === 'view' && (
              <>
                <Button variant="outline" size="sm" onClick={cancel}
                         data-testid="agenda-cancel-event-btn">
                  <X className="w-4 h-4 mr-1" /> Annuler l'événement
                </Button>
                <Button variant="destructive" size="sm" onClick={remove}
                         data-testid="agenda-delete-event-btn">
                  <Trash2 className="w-4 h-4 mr-1" /> Supprimer
                </Button>
              </>
            )}
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => onOpenChange(false)}>Fermer</Button>
            {mode === 'view' ? (
              <Button onClick={() => setMode('edit')} data-testid="agenda-edit-event-btn">Modifier</Button>
            ) : (
              <Button onClick={save} disabled={saving} data-testid="agenda-save-event-btn">
                {saving ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Check className="w-4 h-4 mr-1" />}
                Enregistrer
              </Button>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ============================================================================
// Suggestions Panel
// ============================================================================
function SuggestionsPanel({ open, onOpenChange, suggestions, usersList, onAccepted }) {
  const [selected, setSelected] = useState(null);   // suggestion
  const [assignees, setAssignees] = useState([]);
  const [working, setWorking] = useState(false);

  const openSug = (s) => {
    setSelected(s);
    setAssignees([]);
  };

  const toggleAssignee = (uid) => {
    setAssignees((prev) => prev.includes(uid) ? prev.filter((x) => x !== uid) : [...prev, uid]);
  };

  const accept = async () => {
    if (!selected) return;
    setWorking(true);
    try {
      await api.post(`/agenda/suggestions/${selected.suggestion_id}/accept`, {
        assigned_user_ids: assignees,
      });
      toast.success('Événement ajouté à l\'agenda');
      setSelected(null);
      onAccepted();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erreur');
    } finally { setWorking(false); }
  };

  const ignore = async (sid) => {
    setWorking(true);
    try {
      await api.post(`/agenda/suggestions/${sid}/ignore`);
      toast.success('Suggestion ignorée');
      setSelected(null);
      onAccepted();
    } catch (e) { toast.error('Erreur'); }
    finally { setWorking(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="agenda-suggestions-panel">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Sparkles className="w-4 h-4 text-blue-600" />
            RDV détectés depuis vos emails
          </DialogTitle>
        </DialogHeader>

        {suggestions.length === 0 ? (
          <div className="py-10 text-center text-slate-500 text-sm">
            Aucun RDV détecté pour le moment.<br />
            Cliquez sur « Analyser les emails » pour lancer un scan.
          </div>
        ) : selected ? (
          <div className="space-y-3">
            <button className="text-xs text-slate-500 hover:text-slate-900" onClick={() => setSelected(null)}>
              ← Retour à la liste
            </button>
            <div className="border border-slate-200 rounded-lg p-3 bg-slate-50">
              <div className="flex items-start justify-between gap-2">
                <div>
                  <div className="text-sm font-bold text-slate-900">{selected.title}</div>
                  <div className="text-xs text-slate-500">Depuis: {selected.from_email || 'inconnu'} · {selected.mailbox_label || ''}</div>
                </div>
                <span className="px-2 py-0.5 rounded-full text-[10px] font-semibold bg-blue-100 text-blue-800">
                  Confiance {Math.round((selected.confidence || 0) * 100)}%
                </span>
              </div>
              <div className="mt-2 grid grid-cols-2 gap-2 text-xs text-slate-600">
                <div><Clock className="w-3 h-3 inline mr-1" />
                  {selected.start_at ? new Date(selected.start_at).toLocaleString('fr-FR') : '—'}
                </div>
                {selected.location && <div><MapPin className="w-3 h-3 inline mr-1" />{selected.location}</div>}
              </div>
              {selected.signals?.length > 0 && (
                <div className="mt-2 flex flex-wrap gap-1">
                  {selected.signals.map((s) => (
                    <span key={s} className="px-1.5 py-0.5 bg-white border border-slate-200 rounded text-[10px] text-slate-600">{s}</span>
                  ))}
                </div>
              )}
              {selected.description && (
                <div className="mt-2 text-xs text-slate-600 bg-white p-2 rounded border border-slate-200 max-h-32 overflow-y-auto">
                  {selected.description}
                </div>
              )}
            </div>

            <div>
              <label className="text-xs font-semibold text-slate-600 block mb-1">Assigner à</label>
              <div className="flex flex-wrap gap-1.5">
                {usersList.map((u) => {
                  const active = assignees.includes(u.user_id);
                  return (
                    <button key={u.user_id}
                             onClick={() => toggleAssignee(u.user_id)}
                             data-testid={`agenda-sug-assignee-${u.user_id}`}
                             className={`px-2.5 py-1 rounded-full text-xs font-medium border transition-colors ${
                               active
                                 ? 'bg-blue-600 text-white border-blue-600'
                                 : 'bg-white text-slate-700 border-slate-300 hover:bg-slate-50'
                             }`}>
                      {u.name || u.email}
                    </button>
                  );
                })}
              </div>
            </div>

            <div className="flex justify-end gap-2 pt-2 border-t border-slate-100">
              <Button variant="outline" onClick={() => ignore(selected.suggestion_id)} disabled={working}
                       data-testid="agenda-sug-ignore-btn">
                Ignorer
              </Button>
              <Button onClick={accept} disabled={working} data-testid="agenda-sug-accept-btn">
                {working ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Check className="w-4 h-4 mr-1" />}
                Ajouter à l'agenda
              </Button>
            </div>
          </div>
        ) : (
          <div className="space-y-2">
            {suggestions.map((s) => (
              <div key={s.suggestion_id}
                   onClick={() => openSug(s)}
                   data-testid={`agenda-sug-item-${s.suggestion_id}`}
                   className="border border-slate-200 rounded-lg p-3 hover:bg-slate-50 cursor-pointer">
                <div className="flex items-center justify-between gap-2">
                  <div className="min-w-0">
                    <div className="text-sm font-semibold text-slate-900 truncate">{s.title}</div>
                    <div className="text-xs text-slate-500 truncate">
                      {s.start_at ? new Date(s.start_at).toLocaleString('fr-FR') : '—'}
                      {s.location ? ` · ${s.location}` : ''}
                    </div>
                    <div className="text-[10px] text-slate-400 truncate">
                      {s.from_email || ''} · {s.mailbox_label || ''}
                    </div>
                  </div>
                  <span className={`shrink-0 px-2 py-0.5 rounded-full text-[10px] font-semibold ${
                    (s.confidence || 0) >= 0.85 ? 'bg-emerald-100 text-emerald-800'
                      : (s.confidence || 0) >= 0.6 ? 'bg-amber-100 text-amber-800'
                      : 'bg-slate-100 text-slate-700'
                  }`}>
                    {Math.round((s.confidence || 0) * 100)}%
                  </span>
                </div>
                {s.signals?.length > 0 && (
                  <div className="mt-1 flex flex-wrap gap-1">
                    {s.signals.slice(0, 4).map((sg) => (
                      <span key={sg} className="px-1.5 py-0.5 bg-white border border-slate-200 rounded text-[10px] text-slate-600">{sg}</span>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
